/**
 * Human-presence gate for engagement beacons (2026-08-25).
 *
 * WHY. Corporate mail filters fetch and DETONATE every link in a
 * message while deciding whether to deliver it. Microsoft Defender
 * Safe Links runs a full headless Chrome that executes this app — so
 * any beacon that fires on load fires for mail sitting in QUARANTINE,
 * never seen by a human. That put "someone opened your Decision Room"
 * notifications in front of sellers for opens that never happened,
 * which is worse than no data: a seller who says "saw you had a look"
 * to a prospect who never opened it loses the deal's credibility.
 *
 * THE GATE. A promise that resolves on the first real input event —
 * pointer, touch, key, wheel, or scroll. Detonation sandboxes render
 * the page but do not synthesize input, so beacons awaiting this
 * promise never fire for them. Server-side UA screening exists too,
 * but the sandbox presents as ordinary Chrome on Windows; input is the
 * signal it cannot fake from outside.
 *
 * WHAT COUNTS AS PRESENCE — every input modality, deliberately:
 * pointermove covers any mouse user within milliseconds; touchstart
 * and pointerdown cover phones; keydown covers keyboard-only and
 * assistive-tech users (arrow-key scrolling included); wheel and
 * scroll cover trackpads and scroll wheels whose cursor never moves.
 * A real reader cannot read without producing at least one of these.
 *
 * The one human this misses: someone who opens the page and reads the
 * first screenful end to end without ever scrolling or touching
 * anything, then closes the tab. That visit goes unrecorded. Accepted:
 * under-counting an edge case is recoverable; phantom engagement
 * poisons the numbers sellers act on.
 */

const EVENTS = ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel', 'scroll'];

let resolved = false;
let promise = null;

export function humanPresent() {
  if (promise) return promise;
  promise = new Promise((resolve) => {
    if (typeof window === 'undefined') return; // SSR/test: never resolves
    const done = () => {
      resolved = true;
      for (const e of EVENTS) window.removeEventListener(e, done, true);
      resolve();
    };
    for (const e of EVENTS) {
      // Capture phase so a stopPropagation() inside the app can't
      // starve the gate; passive so the listener can never jank
      // scrolling.
      window.addEventListener(e, done, { capture: true, passive: true, once: false });
    }
  });
  return promise;
}

/** Synchronous check, for callers that want to drop rather than wait. */
export function isHumanPresent() {
  return resolved;
}

/**
 * Run a callback once a human is present. If one already is, runs on
 * the next microtask. Returns nothing; the callback simply never runs
 * in an automated session.
 */
export function whenHuman(fn) {
  humanPresent().then(fn).catch(() => {});
}

// Test seam: reset module state between tests.
export function __resetForTests() {
  resolved = false;
  promise = null;
}
